/**
 * Axios-Client mit:
 * - Bearer-Token aus In-Memory-Store (Zustand)
 * - Tenant-Header (X-Tenant-Id)
 * - Auto-Refresh bei 401 (genau einmal pro Request)
 * - withCredentials für Refresh-Cookie
 */
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/auth/store";

const api: AxiosInstance = axios.create({
  baseURL: "/api",
  withCredentials: true,
  timeout: 30_000,
});

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

api.interceptors.request.use((config) => {
  const { accessToken, activeTenantId } = useAuthStore.getState();
  if (accessToken) {
    config.headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (activeTenantId != null) {
    config.headers.set("X-Tenant-Id", String(activeTenantId));
  }
  return config;
});

// ---------------------------------------------------------------------------
// Refresh-Logik — eine einzelne Refresh-Promise wird geteilt, damit parallele
// 401-Antworten nicht 5x das /refresh hammern.
// ---------------------------------------------------------------------------

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await axios.post<{
        access_token: string;
        expires_in: number;
      }>("/api/auth/refresh", null, { withCredentials: true });
      const newToken = res.data.access_token;
      // Token im Store aktualisieren ohne den restlichen User-Zustand zu verlieren
      useAuthStore.setState({ accessToken: newToken });
      return newToken;
    } catch {
      useAuthStore.getState().clear();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequest | undefined;
    if (!original) throw error;

    const isAuthEndpoint =
      typeof original.url === "string" && original.url.startsWith("/auth/");

    if (error.response?.status === 401 && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers.set("Authorization", `Bearer ${newToken}`);
        return api.request(original);
      }
    }
    throw error;
  }
);

export default api;
